"use client";
import LyricsIcon from "@mui/icons-material/Lyrics";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { useAudioOpen } from "~/components/audio/useAudio";
import Link from "~/components/Link";


export default function FabCreate() {
  const [mainCtaInView, setMainCtaInView] = useState<boolean>(true);
  const { bigPlayerOpen, miniPlayerOpen } = useAudioOpen();

  useEffect(() => {
    let observedEl: Element | null = null;
    let noElTimer: number | null = null;
    // Pixels of guard space to treat element as "in view" slightly
    // before/after it actually crosses the viewport edge.
    const VIEW_GUARD = 200;
    // Delay before assuming the main CTA is truly absent. Avoids
    // flicker when the CTA mounts a few ticks after this effect runs.
    const NO_EL_DELAY = 150;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) =>
          setMainCtaInView(Boolean(entry.isIntersecting)),
        );
      },
      { root: null, rootMargin: `0px 0px ${VIEW_GUARD}px 0px`, threshold: 0 },
    );

    // Helper: any part of element visible in viewport; also re-observe if element node changed
    const checkVisibility = () => {
      // If user is within the first N pixels from the top, always hide FAB
      // (treat main CTA as "in view") to avoid duplicate CTAs near the top.
      const FIRST_HIDE_PIXELS = 800;
      const scrollY = window.scrollY ?? window.pageYOffset ?? 0;
      if (scrollY < FIRST_HIDE_PIXELS) {
        setMainCtaInView(true);
        return;
      }

      const el = document.getElementById("main-cta-create");

      if (el && el !== observedEl) {
        if (observedEl) obs.unobserve(observedEl);
        try {
          obs.observe(el);
        } catch (e) {
          /* ignore */
        }
        observedEl = el;
      }

      if (!el) {
        // If the CTA is not present right now, wait a short delay before
        // concluding it's absent. This prevents a brief moment where the FAB
        // appears and the CTA mounts immediately after, causing both to be
        // visible at the same time.
        if (noElTimer == null) {
          noElTimer = window.setTimeout(() => {
            noElTimer = null;
            setMainCtaInView(false);
          }, NO_EL_DELAY);
        }
        return;
      }

      // If we've found the CTA, cancel any pending "no element" timer.
      if (noElTimer) {
        clearTimeout(noElTimer);
        noElTimer = null;
      }

      const rect = el.getBoundingClientRect();
      const guard = VIEW_GUARD;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // Consider the element "in view" if it is within the viewport extended
      // by `guard` pixels above and below. This gives a small buffer so the
      // FAB doesn't flicker when the CTA is very near the viewport edge.
      const inView = rect.bottom > -guard && rect.top < vh + guard;
      setMainCtaInView(inView);
    };

    // Initial check
    checkVisibility();

    // Fallback: listen to scroll and resize for edge cases where IO may not fire
    window.addEventListener("scroll", checkVisibility, { passive: true });
    window.addEventListener("resize", checkVisibility);

    return () => {
      obs.disconnect();
      window.removeEventListener("scroll", checkVisibility);
      window.removeEventListener("resize", checkVisibility);
    };
  }, []);

  // Show the FAB only when the main CTA is NOT visible and the big player is not open
  const showFab = !mainCtaInView && !bigPlayerOpen;

  // Entrance: subtle slide + spring; label: delayed slide-in for nicer reveal
  return (
    <AnimatePresence>
      {showFab && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 28, scale: 0 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0 }}
          transition={{ type: "spring", stiffness: 360, damping: 28 }}
          sx={{
            position: "fixed",
            bottom: miniPlayerOpen ? { xs: 76, sm: 80 } : { xs: 20, sm: 24 },
            right: { xs: 8, sm: 16 },
            zIndex: (theme) => theme.zIndex.speedDial,
            transformOrigin: "right bottom",
            // ensure safe-area inset for iOS
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          <Link href="/comanda" style={{ textDecoration: "none" }}>
            <Fab
              color="primary"
              aria-label="Creează"
              size="medium"
              variant="extended"
              sx={{
                // subtle elevation on hover handled by MUI; keep styles minimal
                display: "flex",
                alignItems: "center",
              }}
            >
              <motion.span
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{
                  type: "spring",
                  stiffness: 420,
                  damping: 28,
                  duration: 0.28,
                  delay: 0.1,
                }}
                style={{ display: "inline-flex", alignItems: "center" }}
              >
                <LyricsIcon sx={{ mr: { xs: 0.6, sm: 1 } }} />
              </motion.span>

              <motion.span
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.22, delay: 0.16 }}
                style={{ display: "inline-flex", whiteSpace: "nowrap" }}
              >
                Creează
              </motion.span>
            </Fab>
          </Link>
        </Box>
      )}
    </AnimatePresence>
  );
}
